import type { ComponentPropsWithRef } from 'react';
import { useReducedMotion } from 'motion/react';

import { cn } from '../../lib/cn';
import { useTheme } from '../../theme/ThemeProvider';

/* =============================================================================
   Flow backdrop
   -----------------------------------------------------------------------------
   The approved brand background: woven copper and silver contour ribbons that
   drift on a shared 20-second infinite loop. Native SVG with CSS animation —
   no video, no bitmap, no canvas.

   Scope note: this carries the ANIMATION ONLY. The approved login composition
   also had a pause control and explanatory copy; the control and the copy
   belong to the page and are not here. The one thing the control needs FROM
   here is `paused` — without it a page could only offer a button that does
   nothing, which is a defect, so that prop is the design system's share of it.

   Loaded through <img> rather than inlined, deliberately. The two scenes are
   separate documents with their own `<style>` blocks, `@keyframes` and ids; an
   inlined copy would put two sets of animations and two id namespaces into the
   host page.

   REDUCED MOTION — why there is a second set of files.

   The pack ships a reduced-motion rule, but it cannot fire on these files:

       @media (prefers-reduced-motion: reduce) {
         svg:not([data-motion="play"]) .alliance-scene-N-art * { animation: none !important }
       }

   The scene's own root `<svg>` carries `data-motion="play"`, so that selector
   deliberately never matches it — the pack intends the explicit Play control to
   be the switch, and `data-motion="play"` is what the "on" state of that switch
   looks like in a saved file. On top of that, Chrome does not propagate the
   page's emulated `prefers-reduced-motion` into an `<img>`-loaded SVG document,
   so even without the opt-out the rule would not be reached from here. Measured:
   two frames 2.6s apart were byte-different under `reducedMotion: 'reduce'`.

   So the still is not left to a media query. `flow-*-still.svg` is the same
   artwork with its keyframes and animation declarations removed — the same
   resting state the pack's own rule renders — and the component swaps to it
   from React when the preference is on.

   Motion is never re-implemented or re-timed. The scene owns its own duration,
   easing and loop, so the approved artwork cannot drift from a re-typed copy.
   ============================================================================= */

const SCENES = {
  light: { animated: '/flow/flow-light.svg', still: '/flow/flow-light-still.svg' },
  dark: { animated: '/flow/flow-dark.svg', still: '/flow/flow-dark-still.svg' },
} as const;

export type FlowTone = 'light' | 'dark' | 'auto';

export interface FlowBackdropProps extends ComponentPropsWithRef<'div'> {
  /**
   * Which scene to paint. `auto` follows the resolved theme, which is what a
   * page wants; the explicit values exist so both can be shown side by side.
   */
  tone?: FlowTone;
  /**
   * Strength of the veil between the artwork and the content, 0–1. The approved
   * composition used 0.35: enough to hold text contrast without hiding the
   * ribbons. Set 0 to see the artwork untouched.
   */
  dim?: number;
  /**
   * Freezes the artwork by swapping to the derived still — the same scene with
   * its keyframes removed. This is a frozen POSE, not a suspended animation:
   * nothing records where the cycle had reached, so turning it off starts the
   * loop from the offsets the artwork itself authors (-3s, -4s, -7s) and the
   * viewer returns to the opening frame rather than the one they stopped on.
   * Whatever control is built on it has to be worded to say that, and it is
   * why this prop is a boolean rather than a `toggle()` the component owns.
   */
  paused?: boolean;
  /**
   * Renders the backdrop in normal flow with a fixed aspect ratio instead of
   * filling its nearest positioned ancestor. The showcase needs this; a real
   * page wants the default.
   */
  inline?: boolean;
  /** Aspect ratio used only when `inline` is set. */
  aspect?: string;
}

export function FlowBackdrop({
  tone = 'auto',
  dim = 0.35,
  paused = false,
  inline = false,
  aspect = '16 / 10',
  className,
  style,
  ...rest
}: FlowBackdropProps) {
  const { resolved } = useTheme();
  const reduceMotion = useReducedMotion();
  const scene = tone === 'auto' ? resolved : tone;
  const src = reduceMotion || paused ? SCENES[scene].still : SCENES[scene].animated;

  // The dark composition veils with the dark ground, the light one with the
  // paper surface. Both are read from tokens rather than written as literals, so
  // the veil follows the theme the moment a token changes.
  const veil =
    scene === 'dark'
      ? `color-mix(in srgb, var(--canvas) ${dim * 100}%, transparent)`
      : `color-mix(in srgb, var(--surface-alt) ${dim * 100}%, transparent)`;

  return (
    <div
      {...rest}
      // Decorative by definition: the ribbons carry no information, so the whole
      // layer is hidden from assistive technology rather than described. The
      // SVG's own <title>/<desc> would otherwise surface as a stray image.
      aria-hidden="true"
      className={cn(
        'overflow-hidden',
        inline ? 'relative w-full' : 'absolute inset-0',
        className,
      )}
      style={inline ? { aspectRatio: aspect, ...style } : style}
    >
      <img
        src={src}
        alt=""
        // `slice` is what the scene itself declares; object-cover reproduces it
        // for the <img> box so the artwork fills without letterboxing.
        className="absolute inset-0 size-full object-cover"
        decoding="async"
        // The scene is the page's ground, so it must not be lazy — deferring it
        // would show a bare canvas colour until it arrives.
        loading="eager"
      />
      <div className="absolute inset-0" style={{ background: veil }} />
    </div>
  );
}
